import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import { CONVERSATION_STATUS } from '../constants/chatbotConstants'
import { invalidateConversations } from './invalidate'
import {
  listConversations,
  getConversation,
  replyToConversation,
  getChatbotAgentName,
  assignConversation,
  assignConversationToBot,
  setConversationStatus,
  deleteConversation,
} from '../api'

export function useConversations(workspaceId, botId, filters = {}) {
  return useQuery({
    queryKey: queryKeys.chatbotConversations(workspaceId, botId, filters),
    queryFn: () => listConversations(workspaceId, botId, filters),
    enabled: !!workspaceId && !!botId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useOpenConversationCount(workspaceId, botId) {
  const { data: count = 0 } = useQuery({
    queryKey: queryKeys.chatbotConversations(workspaceId, botId),
    queryFn: () => listConversations(workspaceId, botId),
    enabled: !!workspaceId && !!botId,
    staleTime: 30 * 1000,
    select: (conversations) =>
      conversations.filter((c) => c.status === CONVERSATION_STATUS.OPEN).length,
  })
  return count
}

export function useConversation(workspaceId, botId, id) {
  return useQuery({
    queryKey: queryKeys.chatbotConversation(workspaceId, botId, id),
    queryFn: () => getConversation(workspaceId, botId, id),
    enabled: !!workspaceId && !!botId && !!id,
    staleTime: 30 * 1000,
  })
}

export function useReplyToConversation(workspaceId, botId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body, assigneeId }) =>
      replyToConversation(workspaceId, botId, id, body, {
        agentName: getChatbotAgentName(),
        assigneeId,
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData(
        queryKeys.chatbotConversations(workspaceId, botId),
        (old) =>
          Array.isArray(old)
            ? old.map((c) => (c.id === vars.id ? data : c))
            : old,
      )
      qc.setQueryData(queryKeys.chatbotConversation(workspaceId, botId, vars.id), data)
      invalidateConversations(qc, workspaceId, botId)
    },
  })
}

export function useAssignConversation(workspaceId, botId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, agentName, assigneeId }) =>
      assignConversation(workspaceId, botId, id, {
        assigneeId: assigneeId != null ? String(assigneeId) : undefined,
        agentName: agentName ?? getChatbotAgentName(),
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData(
        queryKeys.chatbotConversations(workspaceId, botId),
        (old) => (Array.isArray(old) ? old.map((c) => (c.id === vars.id ? data : c)) : old),
      )
      qc.setQueryData(queryKeys.chatbotConversation(workspaceId, botId, vars.id), data)
      invalidateConversations(qc, workspaceId, botId)
    },
  })
}

export function useAssignConversationToBot(workspaceId, botId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id }) => assignConversationToBot(workspaceId, botId, id),
    onSuccess: (data, vars) => {
      qc.setQueryData(
        queryKeys.chatbotConversations(workspaceId, botId),
        (old) => (Array.isArray(old) ? old.map((c) => (c.id === vars.id ? data : c)) : old),
      )
      qc.setQueryData(queryKeys.chatbotConversation(workspaceId, botId, vars.id), data)
      invalidateConversations(qc, workspaceId, botId)
    },
  })
}

export function useSetConversationStatus(workspaceId, botId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }) =>
      setConversationStatus(workspaceId, botId, id, status),
    onSuccess: (data, vars) => {
      qc.setQueryData(
        queryKeys.chatbotConversations(workspaceId, botId),
        (old) => (Array.isArray(old) ? old.map((c) => (c.id === vars.id ? data : c)) : old),
      )
      qc.setQueryData(queryKeys.chatbotConversation(workspaceId, botId, vars.id), data)
      invalidateConversations(qc, workspaceId, botId)
    },
  })
}

export function useDeleteConversation(workspaceId, botId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => deleteConversation(workspaceId, botId, id),
    onSuccess: () => invalidateConversations(qc, workspaceId, botId),
  })
}
